import { useTranslation } from 'react-i18next'
import { useGetConfigurationTypeIds, useGetMarketsQuery, useMarketLanguagesQuery, useViewsByMarketIdQuery } from '#api/tmsApiHooks'
import { useMarkets } from '#api/utils/useMarkets'
import { CombinedEntries, Loading, NotAvailable } from '#components/tabularOverlay/EntryHelpers'
import type { InfoEntry, InfoGroup } from '#components/tabularOverlay/InfoEntry'
import { combineStringEntries } from '#components/tabularOverlay/utils'
import type { FullApiConfiguration } from '#features/Configuration/ApiConfiguration'
import { translateBestEffort } from '#i18n/LocalizedString'
import { formatPartPeriod, isValidDateString, stringToDate, stringToPeriod } from '#utils/DateString'
import { formatDateTime } from '#utils/formatDateTime'

const useOrderCountryEntries = (apiConfiguration: FullApiConfiguration): InfoEntry[] => {
  const { t, i18n } = useTranslation('shared')
  const { status, markets } = useMarkets()
  const usedMarket = markets.find((m) => m.countryCode === apiConfiguration.orderCountry)
  const marketLabel =
    status === 'loading' ? <Loading /> : status === 'error' ? <NotAvailable /> : translateBestEffort(usedMarket?.label, i18n.language)
  const language = usedMarket?.languages?.find((l) => l.languageCode === apiConfiguration.orderLanguage)
  const languageLabel =
    status === 'loading' ? <Loading /> : status === 'error' ? <NotAvailable /> : translateBestEffort(language?.languageLabel, i18n.language)

  const results: InfoEntry[] = [
    {
      entryId: 'orderCountry',
      title: t('configuration.configuration-information.groups.calling-parameters.entries.order-market', 'Order Market'),
      value: combineStringEntries(translateBestEffort(usedMarket?.label, i18n.language), apiConfiguration.orderCountry),
      displayValue: <CombinedEntries primary={marketLabel} secondary={apiConfiguration.orderCountry} />,
    },
    {
      entryId: 'orderLanguage',
      title: t('configuration.configuration-information.groups.calling-parameters.entries.order-language', 'Order Language'),
      value: combineStringEntries(translateBestEffort(language?.languageLabel, i18n.language), apiConfiguration.orderLanguage),
      displayValue: <CombinedEntries primary={languageLabel} secondary={apiConfiguration.orderLanguage} />,
    },
  ]

  if (apiConfiguration.nationalCodeMarketIdOverride) {
    const nationalCodeMarket = markets.find((m) => m.marketId === apiConfiguration.nationalCodeMarketIdOverride)
    const nationalCodeMarketLabel =
      status === 'loading' ? <Loading /> : status === 'error' ? <NotAvailable /> : translateBestEffort(nationalCodeMarket?.label, i18n.language)
    results.push({
      entryId: 'nationalCodeMarket',
      title: t(
        'configuration.configuration-information.groups.calling-parameters.entries.national-code-market-override',
        'National Code Market Override',
      ),
      value: combineStringEntries(translateBestEffort(nationalCodeMarket?.label, i18n.language), apiConfiguration.nationalCodeMarketIdOverride),
      displayValue: <CombinedEntries primary={nationalCodeMarketLabel} secondary={apiConfiguration.nationalCodeMarketIdOverride} />,
    })
  }

  return results
}

export const useMarketEntry = (apiConfiguration: FullApiConfiguration): InfoEntry => {
  const { t } = useTranslation('shared')
  const { data, error, isLoading } = useGetMarketsQuery()

  const market = data?.markets?.find((m) => m.marketId === apiConfiguration.market)
  const marketLabel = isLoading ? <Loading /> : error ? <NotAvailable /> : market?.marketName

  return {
    entryId: 'market',
    title: t('configuration.configuration-information.groups.calling-parameters.entries.market', 'Market'),
    value: combineStringEntries(market?.marketName, apiConfiguration.market),
    displayValue: <CombinedEntries primary={marketLabel} secondary={apiConfiguration.market} />,
  }
}

const useLanguageEntry = (apiConfiguration: FullApiConfiguration): InfoEntry => {
  const { t } = useTranslation('shared')
  const { data, error, isLoading } = useMarketLanguagesQuery({ marketId: apiConfiguration.market })
  const language = data?.listOfLanguage?.find((l) => l.languageID === apiConfiguration.language)
  const languageLabel = isLoading ? <Loading /> : error ? <NotAvailable /> : language?.languageName

  return {
    entryId: 'market-language',
    title: t('configuration.configuration-information.groups.calling-parameters.entries.market-language', 'Market Language'),
    value: combineStringEntries(language?.languageName, apiConfiguration.language),
    displayValue: <CombinedEntries primary={languageLabel} secondary={apiConfiguration.language} />,
  }
}

const useConfigurationTypeEntry = (apiConfiguration: FullApiConfiguration): InfoEntry => {
  const { t } = useTranslation('shared')

  const { data, error, isLoading } = useGetConfigurationTypeIds()
  const configurationType = data?.listOfConfigTypes?.find((c) => c.configTypeID === apiConfiguration.configurationType)
  const configurationTypeLabel = isLoading ? <Loading /> : error ? <NotAvailable /> : configurationType?.configTypeName

  return {
    entryId: 'configuration-type',
    title: t('configuration.configuration-information.groups.calling-parameters.entries.configuration-type', 'Configuration Type'),
    value: combineStringEntries(configurationType?.configTypeName, apiConfiguration.configurationType),
    displayValue: <CombinedEntries primary={configurationTypeLabel} secondary={apiConfiguration.configurationType} />,
  }
}

const useViewEntry = (apiConfiguration: FullApiConfiguration): InfoEntry => {
  const { t } = useTranslation('shared')
  const { data, error, isLoading } = useViewsByMarketIdQuery({ marketId: apiConfiguration.market })
  const viewType = data?.listOfViewHeader?.find((v) => v.viewID === apiConfiguration.view)
  const viewTypeLabel = isLoading ? <Loading /> : error ? <NotAvailable /> : viewType?.viewName

  return {
    entryId: 'view-type',
    title: t('configuration.configuration-information.groups.calling-parameters.entries.view-type', 'View Type'),
    value: combineStringEntries(viewType?.viewName, apiConfiguration.view),
    displayValue: <CombinedEntries primary={viewTypeLabel} secondary={apiConfiguration.view} />,
  }
}

export const useFullApiConfigurationInfo = (apiConfiguration: FullApiConfiguration): InfoGroup => {
  const { t, i18n } = useTranslation('shared')

  const orderCountryEntries = useOrderCountryEntries(apiConfiguration)
  const marketEntry = useMarketEntry(apiConfiguration)
  const languageEntry = useLanguageEntry(apiConfiguration)
  const configurationTypeEntry = useConfigurationTypeEntry(apiConfiguration)
  const viewEntry = useViewEntry(apiConfiguration)

  return {
    groupId: 'calling-parameters',
    title: t('configuration.configuration-information.groups.calling-parameters.title', 'Calling Parameters'),
    entries: [
      ...orderCountryEntries,
      {
        entryId: 'desired-dealer-date',
        title: t('configuration.configuration-information.groups.calling-parameters.entries.desired-dealer-date', 'Desired Dealer Date'),
        value: isValidDateString(apiConfiguration.desiredDealerDate) ? apiConfiguration.desiredDealerDate : undefined,
      },
      {
        entryId: 'is-desired-dealer-date-estimated',
        title: t(
          'configuration.configuration-information.groups.calling-parameters.entries.is-desired-dealer-date-estimated',
          'Is Desired Dealer Date Estimated',
        ),
        value: apiConfiguration.desiredDealerDateIsEstimate ? t('common.label.yes') : t('common.label.no'),
      },
      {
        entryId: 'part-period',
        title: t('configuration.configuration-information.groups.calling-parameters.entries.part-period', 'Part Period'),
        value: isValidDateString(apiConfiguration.partPeriod) ? formatPartPeriod(stringToPeriod(apiConfiguration.partPeriod)) : undefined,
      },
      {
        entryId: 'pricing-date',
        title: t('configuration.configuration-information.groups.calling-parameters.entries.pricing-date', 'Pricing Date'),
        value: isValidDateString(apiConfiguration.pricingDate) ? formatDateTime(i18n, stringToDate(apiConfiguration.pricingDate)) : undefined,
      },
      marketEntry,
      languageEntry,
      configurationTypeEntry,
      viewEntry,
      {
        entryId: 'calling-system',
        title: t('configuration.configuration-information.groups.calling-parameters.entries.calling-system', 'Calling System'),
        value: apiConfiguration.callingSystem ?? undefined,
      },
      {
        entryId: 'back-url',
        title: t('configuration.configuration-information.groups.calling-parameters.entries.back-url', 'Back URL'),
        value: apiConfiguration.backUrl ?? undefined,
      },
    ],
  } satisfies InfoGroup
}
